import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, MessageCircle, Trash2, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import supabase from '@/supabase';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useSession } from '@/hooks/useSessionHook';
import { useStartConversation } from '@/hooks/use-start-conversation';
import type { StoryRailItem } from '@/types/story';

interface StoryViewerProps {
  open: boolean;
  stories: StoryRailItem[];
  initialStoryId: string | null;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => Promise<void> | void;
}

export function StoryViewer({
  open,
  stories,
  initialStoryId,
  onOpenChange,
  onRefresh,
}: StoryViewerProps) {
  const navigate = useNavigate();
  const { session } = useSession();
  const userId = session?.user?.id;
  const { startConversation, isLoading: isStartingConversation } = useStartConversation(userId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [likedStoryIds, setLikedStoryIds] = useState<string[]>([]);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!initialStoryId) return;
    const nextIndex = stories.findIndex((story) => story.id === initialStoryId);
    if (nextIndex >= 0) {
      setCurrentIndex(nextIndex);
    }
  }, [initialStoryId, stories]);

  const currentStory = useMemo(
    () => stories[currentIndex] || null,
    [stories, currentIndex]
  );

  useEffect(() => {
    if (!open || !currentStory || currentStory.isSynthetic) return;

    void supabase.rpc('mark_story_viewed', { p_story_id: currentStory.id });
  }, [open, currentStory]);

  const handleDelete = async () => {
    if (!currentStory?.isOwn) return;

    const { error } = await supabase
      .from('stories')
      .update({
        status: 'deleted',
        deleted_at: new Date().toISOString(),
      })
      .eq('id', currentStory.id);

    if (error) {
      console.error('[StoryViewer] Failed to delete story:', error);
      return;
    }

    onOpenChange(false);
    await onRefresh();
  };

  const isLiked = currentStory ? likedStoryIds.includes(currentStory.id) : false;

  const handleLikeToggle = () => {
    if (!currentStory || currentStory.isOwn) return;

    setLikedStoryIds((prev) =>
      prev.includes(currentStory.id)
        ? prev.filter((storyId) => storyId !== currentStory.id)
        : [...prev, currentStory.id]
    );
  };

  const handleChat = async () => {
    if (!currentStory || currentStory.isOwn) return;

    if (currentStory.botId) {
      onOpenChange(false);
      navigate(`/bot/${currentStory.botId}`);
      return;
    }

    if (currentStory.authorId.startsWith('demo-')) {
      onOpenChange(false);
      navigate('/newchatpage');
      return;
    }

    const result = await startConversation(currentStory.authorId);
    if (!result.success || !result.conversation_id) {
      toast.error(result.message || 'Failed to start chat.');
      return;
    }

    onOpenChange(false);
    navigate(`/chat/${result.conversation_id}`);
  };

  const showPreviousStory = () => {
    setCurrentIndex((prev) => (prev === 0 ? stories.length - 1 : prev - 1));
  };

  const showNextStory = () => {
    setCurrentIndex((prev) => (prev === stories.length - 1 ? 0 : prev + 1));
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.changedTouches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStartRef.current;
    if (!start || stories.length <= 1) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    const horizontalThreshold = 50;
    const verticalThreshold = 40;

    touchStartRef.current = null;

    if (Math.abs(deltaX) < horizontalThreshold || Math.abs(deltaY) > verticalThreshold) {
      return;
    }

    if (deltaX < 0) {
      showNextStory();
      return;
    }

    showPreviousStory();
  };

  if (!currentStory) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[100dvh] w-screen max-w-none translate-x-[-50%] translate-y-[-50%] rounded-none border-0 bg-black p-0 text-white sm:h-[92dvh] sm:w-[min(420px,92vw)] sm:max-w-[420px] sm:rounded-3xl sm:border sm:border-white/10 [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">
          {currentStory.authorName} story
        </DialogTitle>
        <DialogDescription className="sr-only">
          {currentStory.caption || `Viewing ${currentStory.authorName}'s story`}
        </DialogDescription>
        <div className="relative flex h-full w-full flex-col">
          <div className="absolute left-0 right-0 top-0 z-10 h-40 bg-gradient-to-b from-black/80 via-black/45 to-transparent" />
          {!currentStory.isOwn && (
            <div className="absolute inset-x-0 bottom-0 z-10 h-28 bg-gradient-to-t from-black/80 via-black/45 to-transparent" />
          )}

          <div className="absolute left-0 right-0 top-0 z-20 p-4 pt-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 border border-white/20">
                  <AvatarImage src={currentStory.thumbnailUrl} alt={currentStory.authorName} className="object-cover" />
                  <AvatarFallback>{currentStory.authorName.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-semibold">{currentStory.authorName}</p>
                  <p className="text-xs text-white/70">
                    {formatDistanceToNow(new Date(currentStory.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {currentStory.isOwn && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full bg-black/40 text-white hover:bg-black/55"
                    onClick={handleDelete}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full bg-black/40 text-white hover:bg-black/55"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {currentStory.caption && (
              <div className="mt-3 w-full rounded-2xl bg-black/35 px-3 py-2 backdrop-blur-sm">
                <p className="text-sm font-medium leading-snug text-white">{currentStory.caption}</p>
              </div>
            )}
          </div>

          <div
            className="flex h-full items-center justify-center px-3 pb-20 pt-16 sm:px-4 sm:pb-24 sm:pt-20"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {currentStory.mediaType === 'video' ? (
              <video
                src={currentStory.mediaUrl}
                className="max-h-full w-full rounded-2xl object-contain"
                autoPlay
                controls
                muted
                playsInline
              />
            ) : (
              <img
                src={currentStory.mediaUrl}
                alt={currentStory.authorName}
                className="max-h-full w-full rounded-2xl object-contain"
              />
            )}
          </div>

          {!currentStory.isOwn && (
            <div className="absolute inset-x-0 bottom-0 z-20 flex items-end justify-between p-4 pb-6 sm:px-5">
              <Button
                variant="secondary"
                className="h-12 w-12 rounded-full bg-black/60 p-0 text-white hover:bg-black/75"
                onClick={() => void handleChat()}
                disabled={isStartingConversation}
                aria-label="Start chat"
              >
                <MessageCircle className="h-5 w-5" />
              </Button>

              <Button
                variant="secondary"
                className={`h-12 w-12 rounded-full p-0 ${
                  isLiked ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-black/60 text-white hover:bg-black/75'
                }`}
                onClick={handleLikeToggle}
                aria-label={isLiked ? 'Unlike story' : 'Like story'}
              >
                <Heart className={`h-5 w-5 ${isLiked ? 'fill-current' : ''}`} />
              </Button>
            </div>
          )}

          {stories.length > 1 && (
            <>
              <button
                type="button"
                className="absolute left-0 top-0 h-full w-1/4"
                aria-label="Previous story"
                onClick={showPreviousStory}
              >
                <ChevronLeft className="sr-only" />
              </button>
              <button
                type="button"
                className="absolute right-0 top-0 h-full w-1/4"
                aria-label="Next story"
                onClick={showNextStory}
              >
                <ChevronRight className="sr-only" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
